import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileXls, FunnelSimple } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';


const STATUS_LABELS = {
  production_completed: 'Production Completed',
  scheduled_for_dispatch: 'Scheduled',
  partially_dispatched: 'Partially Dispatched',
  dispatched: 'Dispatched'
};

export function InstallationReports() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    dateFrom: '',
    dateTo: '',
    driverName: '',
    vehicleNumber: '',
    executiveName: '',
    customerName: ''
  });

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.driverName.trim()) params.append('driver_name', filters.driverName.trim());
      if (filters.vehicleNumber.trim()) params.append('vehicle_number', filters.vehicleNumber.trim());
      if (filters.executiveName.trim()) params.append('executive_name', filters.executiveName.trim());
      if (filters.customerName.trim()) params.append('customer_name', filters.customerName.trim());
      const { data } = await api.get(`/api/installation/dispatch-report?${params}`);
      setReportData(data);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCSV = () => {
    if (!reportData?.jobs?.length) return;
    const headers = [
      'JO Number', 'Customer Name', 'Executive Name', 'Work Location', 'JO Date',
      'Delivery Date', 'No of Windows', 'Total Sqft', 'Status', 'Dispatch Date',
      'Vehicle Number', 'Driver Name', 'Driver Contact', 'Remarks'
    ];
    const rows = reportData.jobs.map(jo => [
      jo.jo_number, jo.customer_name, jo.executive_name, jo.work_location,
      jo.jo_date, jo.delivery_date, jo.no_of_windows, jo.total_sqft,
      STATUS_LABELS[jo.status] || jo.status,
      jo.last_dispatch_date || '', jo.last_vehicle_number || '',
      jo.last_driver_name || '', '', jo.dispatch_remarks || ''
    ]);
    let csv = headers.join(',') + '\n';
    rows.forEach(row => { csv += row.map(c => `"${c}"`).join(',') + '\n'; });
    csv += `\n"TOTALS","","","","","","${reportData.totals.total_windows}","${reportData.totals.total_sqft}","","","","","",""`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatch_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success(`Exported ${reportData.jobs.length} records`);
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Dispatch Reports</h1>
          <p className="text-sm text-zinc-500 mt-2">View and export dispatch data with advanced filters</p>
        </div>

        {/* Filters */}
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardHeader className="border-b border-zinc-300">
            <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
              <FunnelSimple size={20} weight="bold" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Status</Label>
                <Select value={filters.status} onValueChange={v => updateFilter('status', v)}>
                  <SelectTrigger data-testid="report-status" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="production_completed">Production Completed</SelectItem>
                    <SelectItem value="scheduled_for_dispatch">Scheduled</SelectItem>
                    <SelectItem value="partially_dispatched">Partially Dispatched</SelectItem>
                    <SelectItem value="dispatched">Dispatched</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery From</Label>
                <Input data-testid="report-date-from" type="date" value={filters.dateFrom} onChange={e => updateFilter('dateFrom', e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery To</Label>
                <Input data-testid="report-date-to" type="date" value={filters.dateTo} onChange={e => updateFilter('dateTo', e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Driver Name</Label>
                <Input data-testid="report-driver" placeholder="Search driver..." value={filters.driverName} onChange={e => updateFilter('driverName', e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Vehicle Number</Label>
                <Input data-testid="report-vehicle" placeholder="Search vehicle..." value={filters.vehicleNumber} onChange={e => updateFilter('vehicleNumber', e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Executive Name</Label>
                <Input data-testid="report-executive" placeholder="Search executive..." value={filters.executiveName} onChange={e => updateFilter('executiveName', e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Customer Name</Label>
                <Input data-testid="report-customer" placeholder="Search customer..." value={filters.customerName} onChange={e => updateFilter('customerName', e.target.value)} className="h-11" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        {reportData && (
          <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Jobs</p>
                  <p data-testid="report-total-jobs" className="text-3xl font-light text-zinc-950">{loading ? '...' : reportData.totals.total_jobs}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                  <p data-testid="report-total-windows" className="text-3xl font-light text-zinc-950">{reportData.totals.total_windows}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
                  <p data-testid="report-total-sqft" className="text-3xl font-light text-zinc-950">{reportData.totals.total_sqft}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export */}
        <div className="flex justify-end">
          <Button
            data-testid="export-dispatch-csv"
            onClick={exportCSV}
            disabled={!reportData?.jobs?.length}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11"
          >
            <FileXls size={20} weight="bold" className="mr-2" />
            Export to CSV{reportData?.jobs?.length ? ` (${reportData.jobs.length})` : ''}
          </Button>
        </div>

        {/* Data Table */}
        {reportData && reportData.jobs.length > 0 && (
          <Card data-testid="dispatch-report-table" className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Executive</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Delivery</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Win</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Status</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Dispatch Date</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Vehicle</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Driver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.jobs.map(jo => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-3 font-bold text-zinc-950 whitespace-nowrap">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.executive_name}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.work_location}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{formatDate(jo.delivery_date) || jo.delivery_date}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                        <td className="p-3 whitespace-nowrap">
                          <span className="text-xs font-bold capitalize px-2 py-1 rounded bg-zinc-100 text-zinc-700">
                            {STATUS_LABELS[jo.status] || (jo.status || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{formatDate(jo.last_dispatch_date) || jo.last_dispatch_date || '--'}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.last_vehicle_number || '--'}</td>
                        <td className="p-3 text-zinc-700 whitespace-nowrap">{jo.last_driver_name || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {reportData && reportData.jobs.length === 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-8 text-center text-zinc-500">No data found for the selected filters</CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
